// React Query key catalog — every queryKey used in the frontend.
// Backend should return these in data_mutations.query_keys after any tool call
// so the frontend automatically re-fetches the right data.
//
// Format: KEY → what it represents
// Use the first string of the key (the "resource" name) in query_keys[].
// Invalidating ["users"] will also invalidate ["users", page, search, ...] variants.

export const QUERY_KEYS = {

  // ── Users & Roles ──────────────────────────────────────────────────────────
  users:    "users",            // user list (all variants)
  user:     "user",             // single user detail
  roles:    "roles",            // roles list
  role:     "role",             // single role detail
  scopes:   "scopes",           // role permissions/scopes

  // ── CRM / Persons ─────────────────────────────────────────────────────────
  person:               "person",               // persons list
  "person-detail":      "person-detail",        // single person detail
  "person-calls":       "person-calls",
  "person-chats":       "person-chats",
  "person-emails":      "person-emails",
  "person-sms":         "person-sms",
  "person-interactions":"person-interactions",
  "person-attributes":  "person-attributes",
  persons:              "persons",

  // ── Interactions ───────────────────────────────────────────────────────────
  interactions:         "interactions",
  "interaction-details":"interaction-details",

  // ── Sales Team ─────────────────────────────────────────────────────────────
  campaign:    "campaign",       // single sales agent/campaign
  campaigns:   "campaigns",      // sales agents list
  "sales-agent":"sales-agent",   // sales agent detail (persona or agent)
  sales:       "sales",

  // ── Support Team ───────────────────────────────────────────────────────────
  "support-line":  "support-line",   // single support agent
  "support-lines": "support-lines",  // support agents list
  "support-agent": "support-agent",  // support agent / persona

  // ── Command Team ───────────────────────────────────────────────────────────
  "command-agent":       "command-agent",        // single command agent
  "command-agents":      "command-agents",       // command agents list
  "command-sessions":    "command-sessions",     // sessions for an agent
  "command-messages":    "command-messages",     // messages in a session
  "command-team-files":  "command-team-files",   // file manager files
  "preset-parent-categories": "preset-parent-categories",
  "preset-sub-categories":    "preset-sub-categories",
  "user-presets":        "user-presets",
  presets:               "presets",
  "llm-models":          "llm-models",

  // ── Analyst Team ───────────────────────────────────────────────────────────
  "analyst-agent":              "analyst-agent",
  "analyst-agents":             "analyst-agents",
  "analyst-agent-analyses":     "analyst-agent-analyses",
  "analyst-agent-analysis-detail": "analyst-agent-analysis-detail",
  "analyst-views":              "analyst-views",
  "analyst-view-details":       "analyst-view-details",
  "analyst-files":              "analyst-files",

  // ── Computer Use Team ──────────────────────────────────────────────────────
  "task":            "task",
  "tasks":           "tasks",
  "tasks-list":      "tasks-list",
  "task-runs":       "task-runs",
  "task-run-detail": "task-run-detail",
  "task-steps":      "task-steps",
  "task-step-runs":  "task-step-runs",
  "task-summary":    "task-summary",

  // ── Documents ──────────────────────────────────────────────────────────────
  datasources:       "datasources",
  "documents-files": "documents-files",
  "kb-docs":         "kb-docs",

  // ── Integrations ───────────────────────────────────────────────────────────
  "integrations-apps":        "integrations-apps",
  "integrations-custom-apps": "integrations-custom-apps",
  "trigger-catalog":          "trigger-catalog",
  "trigger-events":           "trigger-events",
  "third-party-apps":         "third-party-apps",
  "tools":                    "tools",

  // ── Billing ────────────────────────────────────────────────────────────────
  wallet:              "wallet",
  subscription:        "subscription",
  "subscription-plans":"subscription-plans",
  "billing-preference":"billing-preference",
  addons:              "addons",
  "billing-addons":    "billing-addons",

  // ── Settings ───────────────────────────────────────────────────────────────
  "agent-config":     "agent-config",
  webhooks:           "webhooks",
  "security-guardrails":"security-guardrails",
  voices:             "voices",
  "voice-configs":    "voice-configs",
  "voice-providers":  "voice-providers",
  "api-keys":         "api-keys",

  // ── Blueprints ─────────────────────────────────────────────────────────────
  blueprints:          "blueprints",
  "blueprint-details": "blueprint-details",

  // ── Scheduled Jobs ─────────────────────────────────────────────────────────
  "scheduled-jobs": "scheduled-jobs",

  // ── Usage ──────────────────────────────────────────────────────────────────
  "usage-stats":  "usage-stats",
  "agent-usage":  "agent-usage",

  // ── AIOS Assistant ─────────────────────────────────────────────────────────
  "aios-assistant-messages":   "aios-assistant-messages",
  "aios-assistant-preference": "aios-assistant-preference",
  "aios-assistant-llm-models": "aios-assistant-llm-models",
} as const;

// ── Common invalidation groups ────────────────────────────────────────────────
// Backend can use these pre-built groups instead of listing keys individually.
export const INVALIDATION_GROUPS = {
  // After updating a sales agent/campaign
  sales_agent_updated: ["campaign", "campaigns", "sales-agent"],

  // After updating a support agent/line
  support_agent_updated: ["support-line", "support-lines", "support-agent"],

  // After updating a command agent
  command_agent_updated: ["command-agent", "command-agents"],

  // After a command session change (create/delete/pin)
  command_session_changed: ["command-sessions", "command-messages"],

  // After updating user or role
  users_updated: ["users", "user", "roles", "role"],

  // After any document/datasource change
  documents_updated: ["datasources", "documents-files", "kb-docs"],

  // After billing change
  billing_updated: ["wallet", "subscription", "billing-preference"],

  // After settings change
  settings_updated: ["agent-config", "webhooks", "security-guardrails"],

  // After CRM person change
  person_updated: ["person", "person-detail", "persons"],

  // After analyst agent change
  analyst_updated: ["analyst-agent", "analyst-agents", "analyst-agent-analyses"],

  // After task change
  task_updated: ["task", "tasks", "task-runs", "task-steps"],
} as const;
